import { useEffect, useId, useRef, type CSSProperties, type RefObject } from 'react';

/**
 * Jet, the ReviewsJet mascot.
 *
 * ---------------------------------------------------------------------------
 * Why an SVG and not a picture
 * ---------------------------------------------------------------------------
 * Jet was sketched with an image model, but the character on the page is drawn
 * here, shape by shape. A bitmap can only be moved as a whole; this can blink,
 * wave one arm, squash when it lands and change its face between moods, and it
 * stays sharp from a 24px toast to a 320px hero.
 *
 * The body is the centre star of the logo's crown, made plump: five lobes and
 * a core, each stroked in its own fill with round joins, which is what rounds
 * the points without a hand-built path. The arms are separate limbs pinned to
 * the body's edge, so they can rotate without tearing the silhouette — a star
 * point rotated as an "arm" just turned the body into a square.
 *
 * ---------------------------------------------------------------------------
 * Two copies
 * ---------------------------------------------------------------------------
 * The same file lives in the dashboard (`apps/web/src/components/mascot`).
 * The marketing site and the dashboard run different React majors and the
 * site takes the shared packages from npm, so this is a copy kept in step
 * rather than a dependency — the same call as `ColorPicker`. It uses nothing
 * but React and the `jet-*` classes in `jet.css`.
 */

export type JetMood =
  | 'idle'
  | 'wave'
  | 'cheer'
  | 'think'
  | 'sleep'
  | 'wow'
  | 'sad'
  | 'hold'
  | 'work';

/** Arm angles per mood, in degrees, from the resting pose. */
const ARMS: Record<JetMood, [left: number, right: number]> = {
  idle: [0, 0],
  wave: [0, -88],
  cheer: [78, -78],
  think: [0, -150],
  sleep: [-18, 18],
  wow: [42, -42],
  sad: [-26, 26],
  hold: [-128, 128],
  work: [-110, 110],
};

const BODY = [
  'M60 64 L60 86 L36.49 96.36 L39.08 70.8 Z',
  'M60 64 L80.92 70.8 L83.51 96.36 L60 86 Z',
  'M60 64 L47.07 46.2 L60 24 L72.93 46.2 Z',
  'M60 64 L47.07 46.2 L72.93 46.2 L80.92 70.8 L60 86 L39.08 70.8 Z',
  'M39.08 70.8 L21.96 51.64 L47.07 46.2 Z',
  'M72.93 46.2 L98.04 51.64 L80.92 70.8 Z',
];
const LEGS = BODY.slice(0, 2);

/** The moods that set no gaze of their own — thinking looks up, working down. */
const GAZING = new Set<JetMood>(['idle', 'wave', 'cheer', 'wow', 'hold']);

/**
 * Eyes that follow the pointer, a couple of units at most.
 *
 * Written to the element directly rather than through state: it changes on
 * every pointer move, and re-rendering the whole character sixty times a
 * second to slide two ellipses is the kind of cost nobody notices until the
 * page stutters on a cheap phone. A frame is requested only when one is not
 * already pending.
 */
function useGaze(ref: RefObject<SVGGElement | null>, enabled: boolean) {
  useEffect(() => {
    const node = ref.current;
    if (!enabled || !node) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    let frame = 0;
    const onMove = (event: PointerEvent) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const box = node.ownerSVGElement?.getBoundingClientRect();
        if (!box) return;
        const dx = event.clientX - (box.left + box.width / 2);
        const dy = event.clientY - (box.top + box.height / 2);
        const distance = Math.hypot(dx, dy) || 1;
        const reach = Math.min(1, distance / 400);
        node.style.translate = `${((dx / distance) * 2.6 * reach).toFixed(2)}px ${((dy / distance) * 2.2 * reach).toFixed(2)}px`;
      });
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(frame);
      node.style.translate = '';
    };
  }, [ref, enabled]);
}

export function Jet({
  mood = 'idle',
  size = 120,
  className,
  style,
  still = false,
  title,
  gaze = false,
}: {
  mood?: JetMood;
  size?: number | string;
  className?: string;
  style?: CSSProperties;
  /** Hold the pose without the idle loop (hover, blink, flame). */
  still?: boolean;
  /** Give Jet an accessible name; without one it is decoration. */
  title?: string;
  /** Let the eyes follow the pointer. Only in moods whose eyes look ahead. */
  gaze?: boolean;
}) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const pupils = useRef<SVGGElement>(null);
  useGaze(pupils, gaze && GAZING.has(mood));
  const [la, ra] = ARMS[mood];
  const grounded = mood === 'sleep';
  const eyesClosed = mood === 'sleep';

  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      className={['jet', `jet--${mood}`, still ? 'jet--still' : '', className ?? ''].join(' ')}
      style={{ ...style, ['--jet-la' as string]: `${la}deg`, ['--jet-ra' as string]: `${ra}deg` }}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      overflow="visible"
    >
      <defs>
        <linearGradient id={`${uid}-flame`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="0.45" stopColor="#ffe08a" />
          <stop offset="1" stopColor="#ffb21a" />
        </linearGradient>
        <linearGradient id={`${uid}-fade`} x1="0" y1="78" x2="0" y2="104" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#fff" stopOpacity="0" />
          <stop offset="1" stopColor="#fff" />
        </linearGradient>
        <mask id={`${uid}-shade`}>
          <rect width="120" height="120" fill={`url(#${uid}-fade)`} />
        </mask>
      </defs>

      <ellipse className="jet-shadow" cx="60" cy="115" rx={grounded ? 26 : 20} ry="3" fill="#0a0a0b" opacity="0.08" />

      <g className="jet-rig">
        {grounded ? null : (
          <path
            className="jet-flame"
            d="M53.5 95 Q60 118 66.5 95 Q60 99.5 53.5 95Z"
            fill={`url(#${uid}-flame)`}
          />
        )}

        <g className="jet-body">
          <g fill="#1f5bf0" stroke="#1f5bf0" strokeWidth="15" strokeLinejoin="round">
            {BODY.map((d) => (
              <path key={d} d={d} />
            ))}
          </g>
          <g
            fill="#1442c2"
            stroke="#1442c2"
            strokeWidth="15"
            strokeLinejoin="round"
            opacity="0.5"
            mask={`url(#${uid}-shade)`}
          >
            {LEGS.map((d) => (
              <path key={d} d={d} />
            ))}
          </g>
          <path
            d="M46 34 Q52 26 58 30"
            stroke="#fff"
            strokeOpacity="0.45"
            strokeWidth="4"
            strokeLinecap="round"
            fill="none"
          />

          <ellipse cx="42.5" cy="67" rx="4.2" ry="2.6" fill="#ff8fb1" opacity="0.75" />
          <ellipse cx="77.5" cy="67" rx="4.2" ry="2.6" fill="#ff8fb1" opacity="0.75" />

          {eyesClosed ? (
            <path
              d="M46 60 q4 3 8 0 M66 60 q4 3 8 0"
              stroke="#0a0a0b"
              strokeWidth="2.4"
              fill="none"
              strokeLinecap="round"
            />
          ) : (
            <g className="jet-eyes">
              <g className="jet-pupils" ref={pupils}>
                <ellipse cx="50" cy="59" rx="4.4" ry="5.8" fill="#0a0a0b" />
                <ellipse cx="70" cy="59" rx="4.4" ry="5.8" fill="#0a0a0b" />
                <circle cx="51.6" cy="56.6" r="1.7" fill="#fff" />
                <circle cx="71.6" cy="56.6" r="1.7" fill="#fff" />
              </g>
            </g>
          )}

          <Mouth mood={mood} />
        </g>

        {mood === 'hold' ? <HeldStar /> : null}

        <g stroke="#1747cf" strokeWidth="9" strokeLinecap="round" fill="none">
          <path className="jet-arm jet-arm--right" d="M86 75 L96 81" />
          <path className="jet-arm jet-arm--left" d="M34 75 L24 81" />
        </g>
      </g>

      <Extras mood={mood} />
    </svg>
  );
}

function Mouth({ mood }: { mood: JetMood }) {
  const ink = '#0a0a0b';
  switch (mood) {
    case 'cheer':
      return (
        <g>
          <path d="M54.5 67.5 q5.5 8 11 0 z" fill={ink} strokeLinejoin="round" stroke={ink} strokeWidth="1" />
          <path d="M57.5 71.6 q2.5 1.6 5 0" stroke="#ff7a93" strokeWidth="2" fill="none" strokeLinecap="round" />
        </g>
      );
    case 'wow':
      return <ellipse cx="60" cy="70" rx="3.2" ry="3.8" fill={ink} />;
    case 'sleep':
      return <ellipse cx="60" cy="70" rx="2" ry="1.6" fill={ink} />;
    case 'sad':
      return (
        <path d="M55.5 71.5 q4.5 -3.6 9 0" stroke={ink} strokeWidth="2.4" fill="none" strokeLinecap="round" />
      );
    case 'think':
    case 'work':
      return <path d="M56 69.5 q2 -1.4 4 0 t4 0" stroke={ink} strokeWidth="2.2" fill="none" strokeLinecap="round" />;
    default:
      return (
        <path d="M55.5 68.5 q4.5 4 9 0" stroke={ink} strokeWidth="2.4" fill="none" strokeLinecap="round" />
      );
  }
}

/** A five-point star centred on (x, y). */
function starPath(x: number, y: number, r: number) {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const a = ((-90 + i * 36) * Math.PI) / 180;
    const rad = i % 2 === 0 ? r : r * 0.45;
    pts.push(`${(x + rad * Math.cos(a)).toFixed(2)} ${(y + rad * Math.sin(a)).toFixed(2)}`);
  }
  return `M${pts.join(' L')}Z`;
}

function HeldStar() {
  return (
    <path
      className="jet-held"
      d={starPath(60, 86, 13)}
      fill="#ffb21a"
      stroke="#ffb21a"
      strokeWidth="3"
      strokeLinejoin="round"
    />
  );
}

const CONFETTI = [
  { x: 14, y: 30, c: '#ffb21a', r: 5, d: 0 },
  { x: 104, y: 22, c: '#1f5bf0', r: 4, d: 0.15 },
  { x: 110, y: 66, c: '#6d3cf5', r: 3.5, d: 0.3 },
  { x: 8, y: 72, c: '#ff8fb1', r: 3.5, d: 0.45 },
  { x: 92, y: 6, c: '#ffb21a', r: 3, d: 0.6 },
  { x: 30, y: 6, c: '#6d3cf5', r: 3, d: 0.75 },
];

function Extras({ mood }: { mood: JetMood }) {
  if (mood === 'cheer' || mood === 'wow') {
    return (
      <g className="jet-confetti">
        {CONFETTI.map((p) => (
          <path
            key={`${p.x}-${p.y}`}
            d={starPath(p.x, p.y, p.r)}
            fill={p.c}
            stroke={p.c}
            strokeWidth="1.2"
            strokeLinejoin="round"
            style={{ animationDelay: `${p.d}s` }}
          />
        ))}
      </g>
    );
  }
  if (mood === 'think') {
    return (
      <text className="jet-think" x="96" y="30" fontSize="26" fontWeight="800" fill="#1f5bf0" fontFamily="inherit">
        ?
      </text>
    );
  }
  if (mood === 'sleep') {
    return (
      <g className="jet-zzz" fill="#8a94a6" fontWeight="800" fontFamily="inherit">
        <text x="90" y="34" fontSize="14">z</text>
        <text x="100" y="22" fontSize="11">z</text>
        <text x="108" y="12" fontSize="8">z</text>
      </g>
    );
  }
  if (mood === 'sad') {
    return <path className="jet-drop" d="M88 40 q4 6 0 9 q-4 -3 0 -9z" fill="#7fb2ff" />;
  }
  if (mood === 'work') {
    return (
      <g className="jet-sparks" fill="#ffb21a">
        <path d={starPath(100, 30, 5)} />
        <path d={starPath(16, 38, 4)} style={{ animationDelay: '0.4s' }} />
        <path d={starPath(104, 70, 3.5)} style={{ animationDelay: '0.8s' }} />
      </g>
    );
  }
  return null;
}
